import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number; // Percentage of viewport height (default: 0.75 for 3/4 from top, 1/4 from bottom)
  triggerOnce?: boolean; // Whether to trigger animation only once
}

export const useScrollAnimation = (options: UseScrollAnimationOptions = {}) => {
  const { threshold = 0.75, triggerOnce = true } = options;
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleScroll = () => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const triggerPoint = viewportHeight * threshold;

      // Check if element is in the trigger zone (when it reaches threshold of viewport)
      // and we're scrolling down (element is below the trigger point)
      const isInView = rect.top <= triggerPoint && rect.bottom >= 0;

      if (isInView && !hasAnimated) {
        setIsVisible(true);
        if (triggerOnce) {
          setHasAnimated(true);
        }
      } else if (!triggerOnce && !isInView) {
        setIsVisible(false);
      }
    };

    // Check on mount
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [threshold, triggerOnce, hasAnimated]);

  return { elementRef, isVisible };
};
